import { useEffect } from 'react';

import { fg } from '@confluence/feature-gating';

import {
	ANALYTICS_OPERATIONAL_ACTION,
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_UI_SUBJECT,
} from '../../constants/snippetAnalytics.ts';
import {
	setOnFetchComplete,
	setOnFetchScopeError,
	type FetchPerformanceMetrics,
	type FetchScopeErrorInfo,
} from '../../store/actions/fetchActions.ts';

import { useSnippetAnalytics } from './useSnippetAnalytics.ts';

/**
 * Registers analytics callbacks for snippet fetch performance metrics and per-scope
 * fetch failures.
 *
 * On successful fetch: fires a FETCHED operational event with performance metrics
 * (snippetCount, userScopeCount, globalScopeCount, payloadSizeBytes, durationMs).
 *
 * On per-scope failure: fires a snippetApiFetch FAILED operational event with
 * scope and (when available) statusCode attributes, making fetch failures visible
 * in the reliability dashboard. The raw error message is intentionally omitted to
 * avoid leaking server response bodies (potential PII/UGC) into analytics. This
 * per-scope failure event is gated behind `platform_editor_blocks_patch_3` so it
 * can be rolled out alongside the rest of the EDITOR-7783 blocks fast-follows.
 *
 * Call this from a component that is mounted before fetchSnippets() runs
 * (e.g. SnippetsProvider).
 */
export const useSnippetsFetchAnalytics = (): void => {
	const { fireOperationalAnalytic, fireFailedAnalytic } = useSnippetAnalytics();

	useEffect(() => {
		setOnFetchComplete((metrics: FetchPerformanceMetrics) => {
			fireOperationalAnalytic({
				actionSubject: ANALYTICS_UI_SUBJECT.SNIPPETS,
				action: ANALYTICS_OPERATIONAL_ACTION.FETCHED,
				attributes: metrics,
			});
		});

		if (fg('platform_editor_blocks_patch_3')) {
			setOnFetchScopeError((info: FetchScopeErrorInfo) => {
				fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_FETCH, {
					scope: info.scope,
					...(info.statusCode !== undefined && { statusCode: info.statusCode }),
				});
			});
		}

		return () => {
			setOnFetchComplete(null);
			setOnFetchScopeError(null);
		};
		// `fireOperationalAnalytic` and `fireFailedAnalytic` are memoised in
		// useSnippetAnalytics, so they are referentially stable and safe to list here.
	}, [fireOperationalAnalytic, fireFailedAnalytic]);
};
